import { dequal } from 'dequal';
import PropTypes from 'prop-types';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

// Components
import { List } from '@/components/base/Lists/List';
import AnalyticsGrid from '@/components/layouts/wrappers/AnalyticsGrid';

export const RenderAnalyticsItem = ({
    config,
    analytics,
    dates,
    network,
    period,
    analyticsState,
    albumId = null,
    reloadStats = false,
    ...props
}) => {
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const userNumbersFormat = {
        number: user?.settings?.numbers_format ?? 'comma',
        date: {
            timezone: user?.settings?.timezone,
            format: user?.settings?.date_format
        },
        time: {
            format: user?.settings?.time_format
        }
    };

    switch (config.type) {
        case 'kpi':
            if (albumId) {
                return (
                    <AnalyticsGrid.Item size={config.size} key={config.id}>
                        <config.element
                            {...props}
                            onReset={analyticsState?.mutateAnalytics}
                            isLoading={
                                analyticsState?.isLoadingAnalytics ||
                                analyticsState?.isLoadingPermissions ||
                                analyticsState?.isValidatingAnalytics
                            }
                            data={
                                !analyticsState?.isLoadingAnalytics && analytics
                                    ? analytics[config.id]
                                    : {}
                            }
                            config={{
                                ...config,
                                start: dates?.previous?.start,
                                end: dates?.previous?.end
                            }}
                            userNumbersFormat={userNumbersFormat}
                            renderLoader={() => <config.loader config={config} />}
                            albumFooter={Boolean(
                                analytics &&
                                    analytics?.[config.id]?.value !== null &&
                                    analytics?.[config.id]?.type_split
                            )}
                        />
                    </AnalyticsGrid.Item>
                );
            }
            return (
                <AnalyticsGrid.Item size={config.size} key={config.id}>
                    <config.element
                        {...props}
                        onReset={analyticsState?.mutateAnalytics}
                        isLoading={
                            analyticsState?.isLoadingAnalytics ||
                            analyticsState?.isLoadingPermissions ||
                            analyticsState?.isValidatingAnalytics
                        }
                        data={
                            !analyticsState?.isLoadingAnalytics && analytics
                                ? analytics[config.id]
                                : {}
                        }
                        config={{
                            ...config,
                            start: dates?.previous?.start,
                            end: dates?.previous?.end,
                            network
                        }}
                        userNumbersFormat={userNumbersFormat}
                        renderLoader={() => <config.loader config={config} />}
                    />
                </AnalyticsGrid.Item>
            );
        case 'chart':
            return (
                <AnalyticsGrid.Item size={config.size} key={config.id}>
                    <config.element
                        {...props}
                        onReset={analyticsState?.mutateAnalytics}
                        isLoading={
                            analyticsState?.isLoadingAnalytics ||
                            (!analyticsState?.isLoadingAnalytics &&
                                analytics &&
                                dequal(analytics[config.id], {}))
                        }
                        periodData={{
                            periodStart: period.periodStart,
                            periodEnd: period.periodEnd,
                            period: period.period,
                            compareTo: period.compareTo
                        }}
                        setRequestParams={analyticsState?.setRequestParams}
                        setState={analyticsState?.setState}
                        data={
                            !analyticsState?.isLoadingAnalytics && analytics
                                ? {
                                      ...config,
                                      ...analytics[config.id]
                                  }
                                : config
                        }
                        userNumbersFormat={{
                            number: user?.settings?.numbers_format ?? 'comma',
                            date: {
                                timezone: user?.settings?.timezone,
                                format: user?.settings?.date_format
                            }
                        }}
                    />
                </AnalyticsGrid.Item>
            );
        case 'widget':
            return (
                <AnalyticsGrid.Item size={config.size} key={config.id}>
                    <config.element
                        {...props}
                        config={config}
                        sortFields={config.sortFields}
                        page={config.page}
                        albumId={albumId}
                        reloadStats={reloadStats}
                        numberFormat={user?.settings?.numbers_format ?? 'comma'}
                        periodData={{
                            period: period.period,
                            start: period.periodStart,
                            end: period.periodEnd,
                            compareTo: period.compareTo
                        }}
                        network={network}
                    />
                </AnalyticsGrid.Item>
            );
        case 'list':
            return (
                <AnalyticsGrid.Item size={config.size} key={config.id}>
                    <List
                        {...props}
                        network={network}
                        onReset={analyticsState?.mutateAnalytics}
                        isLoading={analyticsState?.isLoadingAnalytics}
                        numberFormat={user?.settings?.numbers_format ?? 'comma'}
                        data={
                            !analyticsState?.isLoadingAnalytics && analytics
                                ? analytics[config.id]
                                : []
                        }
                        config={{
                            ...config,
                            start: dates?.previous?.start,
                            end: dates?.previous?.end
                        }}
                    />
                </AnalyticsGrid.Item>
            );
        case 'table':
            return (
                <AnalyticsGrid.Item size={config.size} key={config.id}>
                    <config.element
                        {...props}
                        network={network}
                        onReset={analyticsState?.mutateAnalytics}
                        isLoading={analyticsState?.isLoadingAnalytics}
                        numberFormat={user?.settings?.numbers_format ?? 'comma'}
                        data={
                            !analyticsState?.isLoadingAnalytics && analytics
                                ? analytics[config.id]
                                : []
                        }
                        config={{
                            ...config,
                            start: dates?.previous?.start,
                            end: dates?.previous?.end
                        }}
                        userNumbersFormat={userNumbersFormat}
                    />
                </AnalyticsGrid.Item>
            );
        default:
            return null;
    }
};

RenderAnalyticsItem.propTypes = {
    config: PropTypes.object,
    analytics: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    network: PropTypes.string,
    period: PropTypes.object,
    analyticsState: PropTypes.object
};
